// src/lib/rentalOpening.js — the four questions asked BEFORE the lease form (owner 2026-09-19).
//
// A compact pre-configuration step over the SAME lease: no new contract type, no second copy of
// any rule. Each answer is written into the fields the lease already stores, through the
// composer's own setValue (which derives completion rule and processing days from the method —
// src/components/desk/ContractComposer.jsx), so the server, the renderer and the payment-stage
// rules see exactly what they saw before.
//
//   1. inspection  — did the tenant see the flat?      → remote_transaction, tenant_inspected_in_person
//   2. handover    — when are the keys handed over?     → key_handover_timing (lease v11+) — a PLAN only;
//                                                         the actual time is the Annex A / handover event
//   3. method      — how is the payment at signing made → payment_method (+ completion rule, days)
//   4. recurring   — monthly rent the same way?          → UI state; "same" keeps the two stages in step
//
// 🔑 THE FACTS ARE INDEPENDENT. No answer is inferred from another: a remote tenant may pay by
//    instant transfer, a tenant who inspected in person may pay by SWIFT and take the keys later.
//    The ONE link that is a rule, not an inference, is the server's: a remote signing cannot be
//    paid in cash (lib/remote_rental.js) — so that option is refused with its reason, never
//    silently replaced.
// Pure: no React, no i18n. Tested by rentalOpening.test.mjs.

export const OPENING_METHODS = ['cash', 'instant_transfer', 'local_bank_transfer', 'international_wire', 'other']
export const INSPECTION = ['in_person', 'remote_inspected', 'remote_media']
export const HANDOVER = ['tenancy_start', 'later_by_arrangement']
export const RECURRING = ['same_as_initial', 'specified_in_contract']
const VALID = { inspection: INSPECTION, handover: HANDOVER, method: [...OPENING_METHODS, 'card_or_electronic_payment'], recurring: RECURRING }

/** Which of the four questions this template asks (Q2 only where the lease carries the field). */
export function openingQuestions(fields) {
  const has = k => (fields || []).some(f => f && f.key === k)
  if (!has('remote_transaction') || !has('payment_method') || !has('recurring_payment_method')) return []
  return ['inspection', ...(has('key_handover_timing') ? ['handover'] : []), 'method', 'recurring']
}

/** The answers a SAVED lease already gives — a stored contract never asks again. */
export function openingFromValues(values, { monthlySame = null } = {}) {
  const v = values || {}
  const method = v.payment_method === 'card_or_electronic_payment' ? 'card_or_electronic_payment'
    : OPENING_METHODS.includes(v.payment_method) ? v.payment_method : null
  return {
    inspection: v.remote_transaction === true
      ? (v.tenant_inspected_in_person === 'yes' ? 'remote_inspected' : 'remote_media')
      : 'in_person',
    handover: HANDOVER.includes(v.key_handover_timing) ? v.key_handover_timing : null,
    method,
    recurring: monthlySame == null ? null : monthlySame ? 'same_as_initial' : 'specified_in_contract',
  }
}

/** A remote signing cannot be paid in cash (lib/remote_rental.js remoteProblem). */
export const methodAllowed = (method, inspection) => !(String(inspection || '').startsWith('remote_') && method === 'cash')
/** "The same way as the payment at signing" is only possible when that payment can recur to the owner. */
export const sameAllowed = method => method !== 'other'

/** Unanswered (or no longer possible) questions, in order. */
export function openingMissing(answers, questions) {
  const a = answers || {}
  const out = []
  for (const q of questions || []) {
    // an answer the question no longer offers (a draft saved under older choices) is no answer
    if (!a[q] || !(VALID[q] || []).includes(a[q])) out.push(q)
    else if (q === 'method' && !methodAllowed(a.method, a.inspection)) out.push(q)
    else if (q === 'recurring' && a.recurring === 'same_as_initial' && !sameAllowed(a.method)) out.push(q)
  }
  return out
}

/**
 * Ordered [key, value] writes for ONE answer. Never touches another question's facts:
 * the inspection answer sets no payment method, the method answer sets no inspection.
 * `v` is the current values (for the due-day rule the remote lease carries).
 */
export function openingSteps(question, answer, v = {}, { dueDayTouched = false } = {}) {
  if (question === 'inspection') {
    if (answer === 'remote_inspected' || answer === 'remote_media') {
      const steps = [['remote_transaction', true], ['tenant_inspected_in_person', answer === 'remote_inspected' ? 'yes' : 'no'], ['physical_handover_required', true]]
      if (v.key_handover_notice_hours == null || v.key_handover_notice_hours === '') steps.push(['key_handover_notice_hours', 24])
      if (v.defect_report_hours == null || v.defect_report_hours === '') steps.push(['defect_report_hours', 24])
      // the server's remote rule: monthly rent falls due on the tenancy's own day — never over a typed one
      const day = Number(String(v.lease_start || '').slice(8, 10))
      if (day > 0 && !dueDayTouched) steps.push(['due_day', day])
      return steps
    }
    // In person: the remote-only facts go with it (they are hidden, and a hidden answer is no answer).
    return [['remote_transaction', false], ['tenant_inspected_in_person', 'yes'], ['physical_handover_required', false]]
  }
  if (question === 'handover') {
    // A handover plan always means an actual handover still has to be recorded in Annex A.
    // Do not let the earlier in-person answer leave this false when the keys come later.
    const steps = [['key_handover_timing', answer], ['physical_handover_required', true]]
    if (answer === 'later_by_arrangement' && (v.key_handover_notice_hours == null || v.key_handover_notice_hours === ''))
      steps.push(['key_handover_notice_hours', 24])
    return steps
  }
  if (question === 'method') {
    // setValue derives completion rule and processing days from the method (the approved table);
    // "other" is a description the agent writes — its completion rule is "custom".
    const steps = [['payment_method', answer]]
    if (answer === 'other') steps.push(['initial_recipient_type', 'other'], ['initial_completion_rule', 'custom'])
    else if (v.initial_recipient_type === 'other') steps.push(['initial_recipient_type', 'owner'])
    if (answer === 'cash' && v.initial_recipient_type === 'agency_settlement_account') steps.push(['initial_recipient_type', 'owner'])
    return steps
  }
  return []   // 'recurring' is composer state, not a stored fact
}

/** Which signing recipients the main form still offers once the method is known. */
export function routesForMethod(method) {
  if (method === 'cash') return ['owner_cash', 'agent_cash']
  if (method === 'other') return ['other']
  if (method === 'card_or_electronic_payment') return ['owner_card']
  if (['instant_transfer', 'local_bank_transfer', 'international_wire'].includes(method)) return ['owner_transfer', 'agent_transfer', 'agency_transfer']
  return null   // no opening answer: every route, as before
}
